import type {FormController, FormData, SvelteFormsForm} from './types';
import type {Readable, Unsubscriber, Writable} from 'svelte/store';
import {get} from 'svelte/store';
import type {Field} from 'node_modules/svelte-forms/types';
import type {Observable, Subscription} from 'rxjs';
import {BehaviorSubject, Subject, combineLatest, concatMap, merge, ReplaySubject} from 'rxjs';
import type {Validator} from 'svelte-forms';
import {combined, form} from 'svelte-forms';
import {distinctUntilChanged, map, take, tap} from 'rxjs/operators';
import {isEqual} from 'lodash-es';
import {shareWithRefCountAndReplayLast} from 'shared/utils/observables-utils';
import type {ModelInputChangeEvent} from '../../events';
import {createModelInputChangeEvent} from '../../events';
import type {Modifier} from './types';

export abstract class CompositeFormController<MODEL, TYPE_OF_STATE, STATE extends Record<string, TYPE_OF_STATE> = Record<string, TYPE_OF_STATE>> implements FormController<MODEL, STATE> {

    private combinedFields: Readable<Field<STATE>> & {validate: () => Promise<Field<STATE>>};
    private combinedFieldsUnsubscriber: Unsubscriber = null;
    private combinedFields$: Subject<Field<STATE>>;
    private fieldState$: Observable<Field<STATE>>;

    private svelteForm: SvelteFormsForm<STATE>;
    private svelteFormUnsubscriber: Unsubscriber = null;
    private svelteForm$: BehaviorSubject<FormData<STATE>>;
    private formData$: Observable<FormData<STATE>>;

    private modelValue$: Subject<MODEL> = new ReplaySubject(1);
    private modelValue$Subscription: Subscription;

    private modelInputChangeEvent$: Subject<ModelInputChangeEvent<MODEL>> = new Subject();
    private modelInputChangeEvent$Subscription: Subscription;
    private manualInputChangeNotification$: Subject<void> = new Subject();

    private controlsTouched = false;
    private touched$: ReplaySubject<boolean> = new ReplaySubject(1);
    private touched$Subscription: Subscription;
    private pendingAsyncValidation$: ReplaySubject<boolean> = new ReplaySubject(1);
    private pendingAsyncValidation$Subscription: Subscription;

    protected constructor(public readonly id: string,
                          public readonly name: string,
                          protected formControllers: FormController<TYPE_OF_STATE>[],
                          private validators: Validator[],
                          private modifiers: Modifier[]) {
    }

    /**
     * @internal This method is intended for internal use inside value editors module
     * Avoid calling it outside of value editors or universal form without handling editor recomposition
     * During composite form controller recomposition store gets changed, but you are stuck with old reference
     */
    public getFieldStateAsStore(): Readable<Field<STATE>> {
        return this.combinedFields;
    }

    public getFieldState$(): Observable<Field<STATE>> {
        return this.fieldState$;
    }

    public getModelValue$(): Observable<MODEL> {
        return this.modelValue$.pipe(distinctUntilChanged(isEqual));
    }

    public getModelInputChangeEvent$(): Observable<ModelInputChangeEvent<MODEL>> {
        return this.modelInputChangeEvent$;
    }

    public getErrors$(): Observable<string[]> {
        return this.fieldState$.pipe(map((field) => field.errors));
    }

    public async validate(): Promise<void> {
        await Promise.allSettled(this.formControllers.map((controller) => controller.validate()))
    }

    public resetForm(): void {
        this.svelteForm.reset();
    }

    public clearForm(): void {
        this.svelteForm.clear();
    }

    public getFormField(name: string): Writable<Field<TYPE_OF_STATE>> | Readable<Field<TYPE_OF_STATE>> {
        return this.svelteForm.getField(name);
    }

    public getFormData$(): Observable<FormData<STATE>> {
        return this.formData$;
    }

    // svelte-forms can not dynamically add fields => we need to recreate form/combined field from scratch
    public recomposeController() {
        if (this.combinedFieldsUnsubscriber) {
            this.combinedFieldsUnsubscriber(); // disconnect
        }
        if (this.svelteFormUnsubscriber) {
            this.svelteFormUnsubscriber(); // disconnect
        }
        this.modelValue$Subscription.unsubscribe();
        this.modelInputChangeEvent$Subscription.unsubscribe();
        this.touched$Subscription.unsubscribe();
        this.pendingAsyncValidation$Subscription.unsubscribe();
        this.composeController(); // recreate combined filed and form from svelte-forms
        if (this.combinedFieldsUnsubscriber) {
            this.connectCombinedField$(); // reconnect
        }
        if (this.svelteFormUnsubscriber) {
            this.connectSvelteForm$(); // reconnect
        }
    }

    public addValidator(validator: Validator) {
        this.validators.push(validator);
    }

    public addAsyncValidator(validator: Validator) {
        this.validators.push(this.patchAsyncValidator(validator));
    }

    private modify(modelValue) {
        return this.modifiers.reduce((value, functor) => functor(value), modelValue);
    }

    public isTouched$(): Observable<boolean> {
        return this.touched$.asObservable();
    }

    public setTouched(): void {
        this.controlsTouched = true;
        this.touched$.next(this.controlsTouched);
    }

    public triggerInputEvent(): void {
        this.manualInputChangeNotification$.next();
    }

    public isAsyncValidationPending(): Observable<boolean> {
        return this.pendingAsyncValidation$.asObservable();
    }

    public abstract setModelValue(newModelValue: MODEL): void;

    protected addController(newFormController: FormController<TYPE_OF_STATE>): void {
        this.formControllers.push(newFormController);
        this.recomposeController();
    }

    protected removeControllerByName(toBeRemovedControllerName: string): void {
        this.formControllers = this.formControllers.filter((formControl) => formControl.name !== toBeRemovedControllerName);
        this.recomposeController();
    }

    protected abstract combineToModelValue$(formControllers: FormController<TYPE_OF_STATE>[]): Observable<MODEL>;

    protected abstract getModelFromStateValue(stateValue: STATE): MODEL;

    // call this in constructor
    protected initializeController() {
        this.composeController();

        this.combinedFields$ = new BehaviorSubject(get(this.combinedFields));
        this.fieldState$ = this.combinedFields$.pipe(
            distinctUntilChanged(isEqual), // to fix svelte-forms bug of double combined fields emission
            tap({
                subscribe: () => this.connectCombinedField$(),
                unsubscribe: () => this.disconnectCombinedField$()
            }),
            shareWithRefCountAndReplayLast()
        );

        this.svelteForm$ = new BehaviorSubject(get(this.svelteForm));
        this.formData$ = this.svelteForm$.pipe(
            tap({
                subscribe: () => this.connectSvelteForm$(),
                unsubscribe: () => this.disconnectSvelteForm$()
            }),
            shareWithRefCountAndReplayLast()
        );
    }

    private composeController() {
        const fieldStores = this.formControllers.map((formController) => formController.getFieldStateAsStore());

        this.combinedFields = this.combineSvelteFormFields(fieldStores);
        this.svelteForm = form(...fieldStores) as SvelteFormsForm<STATE>; // svelte-forms has not enough conscripting types
        this.modelValue$Subscription = this.combineToModelValue$(this.formControllers).pipe(map((source) => this.modify(source))).subscribe(this.modelValue$);
        this.modelInputChangeEvent$Subscription = this.combineToModelInputChangeEvent$(this.formControllers).subscribe(this.modelInputChangeEvent$);
        this.touched$Subscription = this.combineToTouched$(this.formControllers).subscribe(this.touched$);
        this.pendingAsyncValidation$Subscription = this.combineToPendingAsyncValidation$(this.formControllers).subscribe(this.pendingAsyncValidation$)
    }

    private combineSvelteFormFields(fieldStores: Readable<Field<TYPE_OF_STATE>>[]) {
        const reducer = (fields: Field<TYPE_OF_STATE>[]): STATE => Object.fromEntries(fields.map((field) => ([field.name, field.value]))) as STATE;
        return combined(this.name, fieldStores, reducer, this.validators, {stopAtFirstError: true});
    }

    private combineToModelInputChangeEvent$(formControllers: FormController<TYPE_OF_STATE>[]): Observable<ModelInputChangeEvent<MODEL>> {
        const modelInputChangeEventStreams = formControllers.map((controller) => controller.getModelInputChangeEvent$());
        return merge(...modelInputChangeEventStreams, this.manualInputChangeNotification$).pipe(concatMap(() => this.modelValue$.pipe(take(1), map(createModelInputChangeEvent))));
    }

    private combineToTouched$(formControllers: FormController<TYPE_OF_STATE>[]): Observable<boolean> {
        return combineLatest([...formControllers.map((formController) => formController.isTouched$()), new BehaviorSubject(this.controlsTouched)])
            .pipe(
                map((touchedStates) => touchedStates.some((isTouched) => isTouched === true)),
                distinctUntilChanged()
            );
    }

    private combineToPendingAsyncValidation$(formControllers: FormController<TYPE_OF_STATE>[]): Observable<boolean> {
        return combineLatest([...formControllers.map((formController) => formController.isAsyncValidationPending()), new BehaviorSubject(false)])
            .pipe(
                map((asyncValidationPendingStates) => asyncValidationPendingStates.some((isAsyncValidationPending) => isAsyncValidationPending === true)),
                distinctUntilChanged()
            )
    }

    private connectCombinedField$() {
        this.combinedFieldsUnsubscriber = this.combinedFields.subscribe((value) => this.combinedFields$.next(value));
    }

    private disconnectCombinedField$() {
        this.combinedFieldsUnsubscriber();
        this.combinedFieldsUnsubscriber = null;
    }

    private connectSvelteForm$() {
        this.svelteFormUnsubscriber = this.svelteForm.subscribe((value) => this.svelteForm$.next(value));
    }

    private disconnectSvelteForm$() {
        this.svelteFormUnsubscriber();
        this.svelteFormUnsubscriber = null;
    }

    private patchAsyncValidator(originalValidator: Validator): Validator {
        return (stateValue: STATE) => originalValidator(this.getModelFromStateValue(stateValue));
    }
}